import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Brain, TrendingUp, TrendingDown, AlertTriangle, Target, Users,
  RefreshCw, DollarSign, Clock, Sparkles,
} from "lucide-react";

const fmtMoney = (v) => "£" + (Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const RISK_BADGE = {
  low: "bg-emerald-100 text-emerald-700",
  medium: "bg-amber-100 text-amber-700",
  high: "bg-orange-100 text-orange-700",
  critical: "bg-red-100 text-red-700",
};

const CAP_BADGE = {
  ok: "bg-emerald-100 text-emerald-700",
  tight: "bg-amber-100 text-amber-700",
  over: "bg-red-100 text-red-700",
  unknown: "bg-gray-100 text-gray-500",
};

function Bar({ pct, color = "bg-violet-500" }) {
  return (
    <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
    </div>
  );
}

export default function Intelligence() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/intelligence/predictions");
      setData(data);
    } catch {
      toast.error("Failed to load predictions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const s = data?.summary || {};

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-violet-600 mb-0.5">Predictive Intelligence</p>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Brain className="w-6 h-6 text-violet-500" /> Predictions
          </h1>
          <p className="text-sm text-gray-500 mt-1">Live forecasts from your projects, time data and CRM/HubSpot pipeline.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gray-200 text-sm font-medium text-gray-600 hover:border-violet-300 hover:text-violet-700">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Weighted pipeline", value: fmtMoney(s.weighted_pipeline_value), sub: `${s.open_deals || 0} open deals`, icon: DollarSign, color: "bg-violet-50 text-violet-600" },
          { label: "Margin at risk", value: s.projects_at_margin_risk ?? 0, sub: "projects", icon: TrendingDown, color: "bg-red-50 text-red-600" },
          { label: "Schedule risk", value: s.projects_at_schedule_risk ?? 0, sub: "projects", icon: Clock, color: "bg-amber-50 text-amber-600" },
          { label: "Over capacity", value: s.projects_over_capacity ?? 0, sub: "projects", icon: Users, color: "bg-orange-50 text-orange-600" },
        ].map(({ label, value, sub, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-2xl border border-gray-100 p-4">
            <div className={`w-9 h-9 rounded-xl ${color} grid place-items-center mb-3`}><Icon className="w-4 h-4" /></div>
            <div className="text-2xl font-bold text-gray-900">{value}</div>
            <div className="text-[11px] uppercase tracking-wider text-gray-400 mt-0.5">{label}</div>
            <div className="text-xs text-gray-400">{sub}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-400 text-sm">Computing predictions…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Deal win probability */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h2 className="font-semibold text-gray-800 text-sm flex items-center gap-2 mb-4">
              <Target className="w-4 h-4 text-violet-500" /> Deal win probability
            </h2>
            {(!data?.deals || data.deals.length === 0) ? (
              <p className="text-sm text-gray-400 py-6 text-center">No open deals. Connect HubSpot or add deals in the CRM.</p>
            ) : (
              <div className="space-y-3">
                {data.deals.slice(0, 8).map((d) => (
                  <div key={d.deal_id}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-medium text-gray-800 truncate flex items-center gap-1.5">
                        {d.source === "hubspot" && <Sparkles className="w-3 h-3 text-orange-400" title="HubSpot" />}
                        {d.name}
                      </span>
                      <span className="text-gray-500 shrink-0 ml-2">{d.win_probability}% · {fmtMoney(d.expected_value)}</span>
                    </div>
                    <Bar pct={d.win_probability} color={d.win_probability >= 60 ? "bg-emerald-500" : d.win_probability >= 35 ? "bg-amber-500" : "bg-red-500"} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Margin forecast */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h2 className="font-semibold text-gray-800 text-sm flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-violet-500" /> Margin / cost forecast
            </h2>
            {(!data?.projects || data.projects.length === 0) ? (
              <p className="text-sm text-gray-400 py-6 text-center">No active projects.</p>
            ) : (
              <div className="space-y-3">
                {data.projects.map((p) => (
                  <div key={p.project_id} className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-gray-800 truncate">{p.name}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      {p.margin.predicted_final_margin != null ? (
                        <span className={`text-sm font-semibold ${p.margin.is_at_risk ? "text-red-600" : "text-emerald-700"}`}>
                          {p.margin.predicted_final_margin}%
                        </span>
                      ) : <span className="text-xs text-gray-400">No data</span>}
                      {p.margin.is_at_risk && <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
                      <span className="text-[10px] text-gray-400">({p.margin.confidence})</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Delivery / schedule risk */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h2 className="font-semibold text-gray-800 text-sm flex items-center gap-2 mb-4">
              <Clock className="w-4 h-4 text-violet-500" /> Delivery / schedule risk
            </h2>
            {(!data?.projects || data.projects.length === 0) ? (
              <p className="text-sm text-gray-400 py-6 text-center">No active projects.</p>
            ) : (
              <div className="space-y-3">
                {data.projects.map((p) => (
                  <div key={p.project_id}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-medium text-gray-800 truncate">{p.name}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${RISK_BADGE[p.delivery.risk]}`}>{p.delivery.on_time_probability}% on time</span>
                        {p.delivery.overdue_tasks > 0 && <span className="text-[10px] text-red-500">{p.delivery.overdue_tasks} overdue</span>}
                        {p.delivery.high_or_critical_risks > 0 && <span className="text-[10px] text-amber-600">{p.delivery.high_or_critical_risks} open risk{p.delivery.high_or_critical_risks > 1 ? "s" : ""}</span>}
                      </span>
                    </div>
                    <Bar pct={p.delivery.on_time_probability} color={p.delivery.on_time_probability >= 60 ? "bg-emerald-500" : p.delivery.on_time_probability >= 40 ? "bg-amber-500" : "bg-red-500"} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Resource / capacity */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h2 className="font-semibold text-gray-800 text-sm flex items-center gap-2 mb-4">
              <Users className="w-4 h-4 text-violet-500" /> Resource / capacity
            </h2>
            {(!data?.projects || data.projects.length === 0) ? (
              <p className="text-sm text-gray-400 py-6 text-center">No active projects.</p>
            ) : (
              <div className="space-y-3">
                {data.projects.map((p) => (
                  <div key={p.project_id} className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-gray-800 truncate">{p.name}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-gray-500">{p.capacity.people} ppl · {p.capacity.estimated_hours}h</span>
                      <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 capitalize ${CAP_BADGE[p.capacity.status]}`}>
                        {p.capacity.status === "unknown" ? "—" : `${p.capacity.utilization_pct}%`}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <p className="text-[11px] text-gray-400 mt-6 text-center">
        Forecasts recompute from live project, time and pipeline data. HubSpot deals feed win-probability once the API key is configured in Railway.
      </p>
    </div>
  );
}
